import React from 'react';
import styled from 'styled-components';

import Delta from 'components/Gadget/Delta';
import getComparison from 'components/V5Gadget/Matrix/getComparison';
import describeComparison from 'formatters/describeComparison';

import useStudioMetricValue from './useStudioMetricValue';

// What the change is against, under it
const Caption = styled.div`
  color: var(--color-text-tertiary);
  font-size: 12px;
  line-height: 16px;
  text-align: center;
`;

// A Number block's change against an earlier period, drawn as the smart card
// it saves as draws it: % change, change or both, green or red by the
// metric's "positive delta is good". Nothing until both values are in, nor
// when the earlier value is 0. Fetches the earlier period within the block's
// GadgetScope, as the smart card does.
const ComparisonBadge = ({
  metric,
  current,
  comparison,
  show = 'percentDelta',
  isCaptionHidden = false,
  testId,
}: {
  metric: Metrics.Metric;
  current: number | undefined;
  // The earlier period, as useRenderedComparison gives it (stable, as the
  // date scope depends on it)
  comparison: PersistedComparisonType;
  show?: Studio.BlockComparison['show'];
  // Following the report's comparison, as every such block on it does, so
  // saying what it's against each time is noise
  isCaptionHidden?: boolean;
  testId: string;
}) => {
  const previous = useStudioMetricValue({ metric, comparison });
  const caption = isCaptionHidden ? undefined : describeComparison(comparison);

  if (
    current === undefined ||
    previous.isLoading ||
    previous.isError ||
    previous.value === undefined
  ) {
    return null;
  }

  const { formatting } = metric;
  const { delta: rawDelta, isGood } = getComparison({
    value: current,
    previous: previous.value,
    formatting,
    type: 'delta',
  });
  const { delta: percentDelta } = getComparison({
    value: current,
    previous: previous.value,
    formatting,
    type: 'percentDelta',
  });
  if (!Number.isFinite(percentDelta)) {
    return null;
  }

  return (
    <div data-testid={testId} style={{ width: '100%' }}>
      {show === 'delta' && (
        <Delta
          isGood={isGood}
          delta={rawDelta}
          isRaw
          metricId={metric.id}
          isGrid={false}
        />
      )}
      {show === 'percentDelta' && (
        <Delta
          isGood={isGood}
          delta={percentDelta}
          metricId={metric.id}
          isGrid={false}
        />
      )}
      {show === 'both' && (
        <Delta
          isGood={isGood}
          delta={percentDelta}
          rawDelta={rawDelta}
          isBoth
          metricId={metric.id}
          isGrid={false}
        />
      )}
      {caption && (
        <Caption data-testid={`${testId}-caption`}>{caption}</Caption>
      )}
    </div>
  );
};

export default ComparisonBadge;
